import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { personasApi } from '../services/api'
import useAuthStore from '../stores/authStore'
import { toast } from 'react-hot-toast'
import { useAssistanceTypes } from '../hooks/useAssistanceTypes'
import { getFbMatchMeta } from '../utils/fbMatch'
import { useDebounce } from '../hooks/useDebounce'
import { useRecentSearches } from '../hooks/useRecentSearches'
import { highlight } from '../lib/highlight'
import { Badge, Button, Select } from '../components/ui'
import { tipoIcono, tipoLabel } from '../components/instituciones/tipos'
import { nombreSede } from '../components/comedores/comun'
import { cn } from '../lib/cn'

// SVG Icon Helper
const Icon = ({ path, className = 'w-5 h-5', strokeWidth = 2 }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={strokeWidth} d={path} />
  </svg>
)

// Minimalist Lucide-style Icon Paths
const ICONS = {
  shield: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  sparkle: 'M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-6.857 2.286L12 21l-2.286-6.857L3 12l6.857-2.286L12 3z',
  search: 'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  info: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  filter: 'M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z',
  chevronUp: 'M5 15l7-7 7 7',
  chevronDown: 'M19 9l-7 7-7-7',
  refresh: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  arrowRight: 'M14 5l7 7m0 0l-7 7m7-7H3',
  document: 'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  user: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  phone: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  lock: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  heart: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
  chart: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  copy: 'M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z',
  plus: 'M12 6v6m0 0v6m0-6h6m-6 0H6'
}

// Subcomponent 1: Header / Institutional Identity
const SearchHero = () => (
  <div className="text-center space-y-3 pt-2 pb-1">
    <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/90 backdrop-blur-md border border-[#DCE7E5] shadow-xs text-[#08745D] text-xs font-bold tracking-widest uppercase">
      <Icon path={ICONS.shield} className="w-4 h-4 text-[#0F9F7A]" />
      <span>ACCIÓN SOCIAL • SAN ROQUE</span>
    </div>
    
    <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#262626] tracking-tight leading-none flex items-center justify-center gap-2 flex-wrap">
      <span>Búsqueda</span>
      <span className="text-[#0F9F7A] relative inline-block">
        unificada
        <span className="absolute -top-3 -right-6 text-emerald-400 text-lg sm:text-xl font-normal select-none">✨</span>
      </span>
    </h1>

    <p className="text-[#64748B] text-base sm:text-lg max-w-2xl mx-auto font-normal">
      Conectamos ciudadanos, ayudas y red familiar en un solo lugar.
    </p>
  </div>
)

// Subcomponent 2: Main Search Card with Voice Search
const UnifiedSearch = ({ query, setQuery, onSearch, loading }) => {
  return (
    <div className="w-full max-w-4xl mx-auto">
      <div className="bg-white/95 backdrop-blur-md rounded-3xl border border-[#DCE7E5] shadow-xl shadow-teal-900/5 p-3 sm:p-4 transition-all focus-within:border-[#0F9F7A] focus-within:ring-4 focus-within:ring-[#0F9F7A]/10">
        <form onSubmit={onSearch} className="flex flex-col sm:flex-row items-center gap-2">
          <div className="relative flex-1 flex items-center w-full">
            <Icon path={ICONS.search} className="w-6 h-6 text-[#0F9F7A] absolute left-4 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscá por DNI, teléfono, nombre o apellido…"
              aria-label="Buscar personas por DNI, teléfono, nombre o apellido"
              className="w-full pl-14 pr-4 py-4 text-base sm:text-lg font-medium text-[#262626] placeholder-[#64748B]/70 bg-transparent border-0 focus:outline-none"
              autoFocus
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            {/* Main Search Button */}
            <button
              type="submit"
              disabled={loading}
              className="flex-1 sm:flex-initial px-7 py-3.5 bg-[#0F9F7A] hover:bg-[#08745D] text-white rounded-2xl font-bold text-base shadow-md transition-all flex items-center justify-center gap-2 hover:translate-y-[-1px] disabled:opacity-50"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <span>Buscar</span>
                  <Icon path={ICONS.arrowRight} className="w-5 h-5" />
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Subcomponent 3: Phonetic Search Toggle & New Person Action
const PhoneticSearchToggle = ({ fuzzy, setFuzzy, showInfoTooltip, setShowInfoTooltip }) => (
  <div className="w-full max-w-4xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-3">
    <div className="relative flex items-center gap-2.5">
      <button
        type="button"
        onClick={() => setFuzzy(!fuzzy)}
        className="group flex items-center gap-2.5 cursor-pointer focus:outline-none"
      >
        <span className={cn(
          "relative inline-block h-6 w-11 shrink-0 rounded-full transition-colors duration-200 ease-in-out",
          fuzzy ? "bg-[#0F9F7A]" : "bg-slate-300 group-hover:bg-slate-400"
        )}>
          <span className={cn(
            "inline-block h-5 w-5 rounded-full bg-white shadow-md transform transition duration-200 ease-in-out mt-0.5",
            fuzzy ? "translate-x-5.5" : "translate-x-0.5"
          )} />
        </span>
        <span className={cn("text-sm font-semibold transition-colors", fuzzy ? "text-[#08745D]" : "text-[#64748B]")}>
          Búsqueda fonética inteligente
        </span>
      </button>

      {/* Info Tooltip Icon */}
      <div className="relative">
        <button
          type="button"
          onMouseEnter={() => setShowInfoTooltip(true)}
          onMouseLeave={() => setShowInfoTooltip(false)}
          onClick={() => setShowInfoTooltip(!showInfoTooltip)}
          className="text-slate-400 hover:text-[#0F9F7A] transition-colors p-1"
          aria-label="Información sobre búsqueda fonética"
        >
          <Icon path={ICONS.info} className="w-4 h-4" />
        </button>

        {showInfoTooltip && (
          <div className="absolute left-0 bottom-full mb-2 w-64 p-3 bg-[#262626] text-white text-xs rounded-xl shadow-xl z-20 animate-in fade-in duration-150">
            Encuentra coincidencias aunque el nombre esté escrito de forma diferente (ej. González / Gonzáles).
          </div>
        )}
      </div>
    </div>

    {/* Secondary Action: Nueva Persona */}
    <Link
      to="/personas/nueva"
      className="inline-flex items-center gap-1.5 text-sm font-bold text-[#0F9F7A] hover:text-[#08745D] transition-colors hover:underline"
    >
      <Icon path={ICONS.plus} className="w-4 h-4" />
      <span>Nueva persona</span>
    </Link>
  </div>
)

// Todos los filtros en blanco. Los de cruce (comedor, inundación, El Niño) buscan
// personas que están en otros programas, aunque no se escriba ningún nombre.
const FILTROS_VACIOS = {
  tipo_asistencia: '', detalle_asistencia: '', nucleo_familiar: '', convive: '', con_familia: '', con_asistencias: '',
  comedor: '', inundacion: '', incluir_hogar: '', elnino: '',
}
const RIESGOS_ELNINO = { directa: 'Inundación directa', medio: 'Riesgo medio', bajo: 'Riesgo bajo' }
const SELECT_CLASS = 'w-full h-11 px-3.5 bg-white border border-[#DCE7E5] rounded-xl text-sm font-medium text-[#262626] focus:outline-none focus:border-[#0F9F7A] focus:ring-2 focus:ring-[#0F9F7A]/15 transition-all'

// Fila de cruces con otros programas dentro de los filtros.
const FiltrosCruce = ({ adminFilters, setAdminFilters, inundaciones }) => (
  <div className="border-t border-[#DCE7E5]/60 pt-4">
    <p className="mb-3 text-xs font-bold uppercase tracking-widest text-[#0F9F7A]">Cruces con otros programas</p>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
      <div>
        <label className="block text-xs font-semibold text-[#262626] mb-1.5">🍽️ Comedores</label>
        <select value={adminFilters.comedor} className={SELECT_CLASS}
          onChange={(e) => setAdminFilters((p) => ({ ...p, comedor: e.target.value }))}>
          <option value="">Todos</option>
          <option value="si">Anotados en un comedor</option>
          <option value="varios">Anotados en más de un comedor</option>
          <option value="tutor">Tutores de chicos anotados</option>
          <option value="encargado">Encargados/as de sede</option>
          <option value="no">Sin comedor</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#262626] mb-1.5">🌊 Inundaciones</label>
        <select value={adminFilters.inundacion} className={SELECT_CLASS}
          onChange={(e) => setAdminFilters((p) => ({ ...p, inundacion: e.target.value, incluir_hogar: e.target.value ? p.incluir_hogar : '' }))}>
          <option value="">Todas las personas</option>
          <option value="cualquiera">Evacuados en cualquier inundación</option>
          {inundaciones.map((i) => (
            <option key={i.id} value={i.id}>Evacuados · {i.nombre} ({String(i.fecha).slice(0, 7).split('-').reverse().join('/')})</option>
          ))}
        </select>
        {adminFilters.inundacion && (
          <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-[#64748B]">
            <input type="checkbox" checked={adminFilters.incluir_hogar === 'true'} className="h-4 w-4"
              onChange={(e) => setAdminFilters((p) => ({ ...p, incluir_hogar: e.target.checked ? 'true' : '' }))} />
            Incluir a quienes tienen a alguien de su hogar evacuado
          </label>
        )}
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#262626] mb-1.5">🌧️ Relevamiento El Niño</label>
        <select value={adminFilters.elnino} className={SELECT_CLASS}
          onChange={(e) => setAdminFilters((p) => ({ ...p, elnino: e.target.value }))}>
          <option value="">Todos</option>
          <option value="si">Relevados (cualquier riesgo)</option>
          {Object.entries(RIESGOS_ELNINO).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      </div>
    </div>
  </div>
)

// Subcomponent 4: Smart Filters Panel
const SmartFilters = ({
  showFilters,
  setShowFilters,
  adminFilters,
  setAdminFilters,
  clearAdminFilters,
  assistanceTypeOptions,
  typesLoading,
  inundaciones = [],
}) => {
  return (
    <div className="w-full max-w-4xl mx-auto bg-white/95 backdrop-blur-md rounded-3xl border border-[#DCE7E5] shadow-lg shadow-teal-900/5 overflow-hidden transition-all">
      {/* Header */}
      <div className="flex items-center justify-between p-5 border-b border-[#DCE7E5]/60 bg-slate-50/40">
        <div className="flex items-center gap-2 text-[#262626] font-bold text-xs uppercase tracking-widest">
          <Icon path={ICONS.filter} className="w-4 h-4 text-[#0F9F7A]" />
          <span>FILTROS INTELIGENTES</span>
        </div>

        <button
          type="button"
          onClick={() => setShowFilters(!showFilters)}
          className="inline-flex items-center gap-1 text-xs font-semibold text-[#64748B] hover:text-[#262626] transition-colors"
        >
          <span>{showFilters ? 'Ocultar filtros' : 'Mostrar filtros'}</span>
          <Icon path={showFilters ? ICONS.chevronUp : ICONS.chevronDown} className="w-4 h-4" />
        </button>
      </div>

      {/* Grid Inputs */}
      {showFilters && (
        <div className="p-6 space-y-4 animate-in fade-in duration-200">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Fila 1 */}
            <div>
              <label className="block text-xs font-semibold text-[#262626] mb-1.5">Tipo de asistencia</label>
              <select
                value={adminFilters.tipo_asistencia}
                onChange={(e) => setAdminFilters(p => ({ ...p, tipo_asistencia: e.target.value }))}
                disabled={typesLoading}
                className="w-full h-11 px-3.5 bg-white border border-[#DCE7E5] rounded-xl text-sm font-medium text-[#262626] focus:outline-none focus:border-[#0F9F7A] focus:ring-2 focus:ring-[#0F9F7A]/15 transition-all"
              >
                <option value="">Todos los tipos</option>
                {assistanceTypeOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#262626] mb-1.5">Detalle de ayuda</label>
              <input
                type="text"
                value={adminFilters.detalle_asistencia}
                onChange={(e) => setAdminFilters(p => ({ ...p, detalle_asistencia: e.target.value }))}
                placeholder="Ej: Alimentos, Salud..."
                className="w-full h-11 px-3.5 bg-white border border-[#DCE7E5] rounded-xl text-sm font-medium text-[#262626] placeholder-[#64748B]/60 focus:outline-none focus:border-[#0F9F7A] focus:ring-2 focus:ring-[#0F9F7A]/15 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#262626] mb-1.5">Núcleo familiar</label>
              <input
                type="text"
                value={adminFilters.nucleo_familiar}
                onChange={(e) => setAdminFilters(p => ({ ...p, nucleo_familiar: e.target.value }))}
                placeholder="Nombre o ID del núcleo"
                className="w-full h-11 px-3.5 bg-white border border-[#DCE7E5] rounded-xl text-sm font-medium text-[#262626] placeholder-[#64748B]/60 focus:outline-none focus:border-[#0F9F7A] focus:ring-2 focus:ring-[#0F9F7A]/15 transition-all"
              />
            </div>

            {/* Fila 2 */}
            <div>
              <label className="block text-xs font-semibold text-[#262626] mb-1.5">Convivencia</label>
              <select
                value={adminFilters.convive}
                onChange={(e) => setAdminFilters(p => ({ ...p, convive: e.target.value }))}
                className="w-full h-11 px-3.5 bg-white border border-[#DCE7E5] rounded-xl text-sm font-medium text-[#262626] focus:outline-none focus:border-[#0F9F7A] focus:ring-2 focus:ring-[#0F9F7A]/15 transition-all"
              >
                <option value="">Todos</option>
                <option value="true">Convivientes</option>
                <option value="false">No convivientes</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#262626] mb-1.5">Grupo familiar</label>
              <select
                value={adminFilters.con_familia}
                onChange={(e) => setAdminFilters(p => ({ ...p, con_familia: e.target.value }))}
                className="w-full h-11 px-3.5 bg-white border border-[#DCE7E5] rounded-xl text-sm font-medium text-[#262626] focus:outline-none focus:border-[#0F9F7A] focus:ring-2 focus:ring-[#0F9F7A]/15 transition-all"
              >
                <option value="">Todos</option>
                <option value="true">Con grupo familiar</option>
                <option value="false">Sin grupo familiar</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#262626] mb-1.5">Asistencia</label>
              <div className="flex items-center gap-2">
                <select
                  value={adminFilters.con_asistencias}
                  onChange={(e) => setAdminFilters(p => ({ ...p, con_asistencias: e.target.value }))}
                  className="w-full h-11 px-3.5 bg-white border border-[#DCE7E5] rounded-xl text-sm font-medium text-[#262626] focus:outline-none focus:border-[#0F9F7A] focus:ring-2 focus:ring-[#0F9F7A]/15 transition-all"
                >
                  <option value="">Todas</option>
                  <option value="true">Con asistencias activas</option>
                  <option value="false">Sin asistencias registradas</option>
                </select>

                <button
                  type="button"
                  onClick={clearAdminFilters}
                  title="Limpiar filtros"
                  className="h-11 px-3 bg-slate-100 hover:bg-slate-200 text-[#64748B] hover:text-[#262626] rounded-xl font-semibold text-xs transition-colors flex items-center justify-center shrink-0 gap-1"
                >
                  <Icon path={ICONS.refresh} className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Limpiar</span>
                </button>
              </div>
            </div>
          </div>
          <FiltrosCruce adminFilters={adminFilters} setAdminFilters={setAdminFilters} inundaciones={inundaciones} />
        </div>
      )}
    </div>
  )
}

// Subcomponent 5: Status Filter Cards (Alta Demanda, Con Asistencia, Sin Ayuda Activa, Instituciones)
const StatusCard = ({ onApplyPreset }) => (
  <div className="w-full max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
    {/* Card 1: Alta Demanda */}
    <div
      onClick={() => onApplyPreset('alta_demanda')}
      className="group cursor-pointer bg-[#FFF1F2] border border-[#FFE4E6] hover:border-rose-300 rounded-2xl p-4 transition-all hover:-translate-y-1 hover:shadow-md flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FECDD3] text-[#9F1239] flex items-center justify-center shrink-0 font-bold">
          <Icon path={ICONS.chart} className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs font-black tracking-wider uppercase text-[#9F1239]">ALTA DEMANDA</div>
          <div className="text-xs text-[#9F1239]/80 font-medium">Requieren atención prioritaria</div>
        </div>
      </div>
      <Icon path={ICONS.arrowRight} className="w-4 h-4 text-[#9F1239] transition-transform group-hover:translate-x-1" />
    </div>

    {/* Card 2: Con Asistencia */}
    <div
      onClick={() => onApplyPreset('con_asistencia')}
      className="group cursor-pointer bg-[#EFF6FF] border border-[#DBEAFE] hover:border-blue-300 rounded-2xl p-4 transition-all hover:-translate-y-1 hover:shadow-md flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#BFDBFE] text-[#1E40AF] flex items-center justify-center shrink-0 font-bold">
          <Icon path={ICONS.heart} className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs font-black tracking-wider uppercase text-[#1E40AF]">CON ASISTENCIA</div>
          <div className="text-xs text-[#1E40AF]/80 font-medium">Reciben ayuda activa</div>
        </div>
      </div>
      <Icon path={ICONS.arrowRight} className="w-4 h-4 text-[#1E40AF] transition-transform group-hover:translate-x-1" />
    </div>

    {/* Card 3: Sin Ayuda Activa */}
    <div
      onClick={() => onApplyPreset('sin_ayuda')}
      className="group cursor-pointer bg-[#FFFBEB] border border-[#FEF3C7] hover:border-amber-300 rounded-2xl p-4 transition-all hover:-translate-y-1 hover:shadow-md flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#FDE68A] text-[#92400E] flex items-center justify-center shrink-0 font-bold">
          <Icon path={ICONS.user} className="w-5 h-5" />
        </div>
        <div>
          <div className="text-xs font-black tracking-wider uppercase text-[#92400E]">SIN AYUDA ACTIVA</div>
          <div className="text-xs text-[#92400E]/80 font-medium">Sin asistencia registrada</div>
        </div>
      </div>
      <Icon path={ICONS.arrowRight} className="w-4 h-4 text-[#92400E] transition-transform group-hover:translate-x-1" />
    </div>

    {/* Card 4: Búsquedas Rápidas por Instituciones */}
    <div
      onClick={() => onApplyPreset('instituciones')}
      className="group cursor-pointer bg-[#F5F3FF] border border-[#EDE9FE] hover:border-violet-300 rounded-2xl p-4 transition-all hover:-translate-y-1 hover:shadow-md flex items-center justify-between gap-3"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#DDD6FE] text-[#6D28D9] flex items-center justify-center shrink-0 font-bold text-lg">
          🏫
        </div>
        <div>
          <div className="text-xs font-black tracking-wider uppercase text-[#6D28D9]">INSTITUCIONES</div>
          <div className="text-xs text-[#6D28D9]/80 font-medium">Búsquedas rápidas por instituciones</div>
        </div>
      </div>
      <Icon path={ICONS.arrowRight} className="w-4 h-4 text-[#6D28D9] transition-transform group-hover:translate-x-1" />
    </div>
  </div>
)

// Subcomponent 6: Quick Search Cards (DNI, Nombre, Teléfono, Institución)
const QuickSearchCard = ({ onSelectQuickSearch }) => (
  <div className="w-full max-w-4xl mx-auto space-y-4 pt-4">
    <div className="text-center">
      <h3 className="text-lg font-extrabold text-[#262626] relative inline-block">
        Búsquedas rápidas
        <span className="block h-0.5 w-12 bg-[#0F9F7A] rounded-full mx-auto mt-1" />
      </h3>
    </div>

    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {/* Quick Search 1: DNI */}
      <div
        onClick={() => onSelectQuickSearch('32456789')}
        className="group cursor-pointer bg-white/90 backdrop-blur-md border border-[#DCE7E5] hover:border-[#0F9F7A] rounded-2xl p-4 transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-teal-900/5 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-emerald-50 text-[#0F9F7A] flex items-center justify-center shrink-0">
            <Icon path={ICONS.document} className="w-5 h-5" />
          </div>
          <div>
            <div className="text-sm font-bold text-[#262626] group-hover:text-[#0F9F7A] transition-colors">Buscar por DNI</div>
            <div className="text-xs font-mono text-[#64748B]">Ej: 32456789</div>
          </div>
        </div>
        <Icon path={ICONS.arrowRight} className="w-4 h-4 text-[#64748B] group-hover:text-[#0F9F7A] transition-transform group-hover:translate-x-1" />
      </div>

      {/* Quick Search 2: Nombre */}
      <div
        onClick={() => onSelectQuickSearch('González María')}
        className="group cursor-pointer bg-white/90 backdrop-blur-md border border-[#DCE7E5] hover:border-[#0F9F7A] rounded-2xl p-4 transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-teal-900/5 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
            <Icon path={ICONS.user} className="w-5 h-5" />
          </div>
          <div>
            <div className="text-sm font-bold text-[#262626] group-hover:text-purple-600 transition-colors">Buscar por nombre</div>
            <div className="text-xs text-[#64748B]">Ej: González María</div>
          </div>
        </div>
        <Icon path={ICONS.arrowRight} className="w-4 h-4 text-[#64748B] group-hover:text-purple-600 transition-transform group-hover:translate-x-1" />
      </div>

      {/* Quick Search 3: Teléfono */}
      <div
        onClick={() => onSelectQuickSearch('3794123456')}
        className="group cursor-pointer bg-white/90 backdrop-blur-md border border-[#DCE7E5] hover:border-[#0F9F7A] rounded-2xl p-4 transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-teal-900/5 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Icon path={ICONS.phone} className="w-5 h-5" />
          </div>
          <div>
            <div className="text-sm font-bold text-[#262626] group-hover:text-blue-600 transition-colors">Buscar por teléfono</div>
            <div className="text-xs font-mono text-[#64748B]">Ej: 3794123456</div>
          </div>
        </div>
        <Icon path={ICONS.arrowRight} className="w-4 h-4 text-[#64748B] group-hover:text-blue-600 transition-transform group-hover:translate-x-1" />
      </div>

      {/* Quick Search 4: Instituciones */}
      <div
        onClick={() => onSelectQuickSearch('Escuela Normal')}
        className="group cursor-pointer bg-white/90 backdrop-blur-md border border-[#DCE7E5] hover:border-[#0F9F7A] rounded-2xl p-4 transition-all hover:-translate-y-1 hover:shadow-lg hover:shadow-teal-900/5 flex items-center justify-between gap-3"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-violet-50 text-violet-700 flex items-center justify-center shrink-0 text-lg">
            🏫
          </div>
          <div>
            <div className="text-sm font-bold text-[#262626] group-hover:text-violet-700 transition-colors">Por instituciones</div>
            <div className="text-xs font-mono text-[#64748B]">Ej: Escuela Normal</div>
          </div>
        </div>
        <Icon path={ICONS.arrowRight} className="w-4 h-4 text-[#64748B] group-hover:text-violet-700 transition-transform group-hover:translate-x-1" />
      </div>
    </div>
  </div>
)

// Subcomponent 7: Security Notice Footer
const SecurityNotice = () => (
  <div className="text-center pt-6 pb-2 text-[#64748B] text-xs font-medium flex items-center justify-center gap-1.5">
    <Icon path={ICONS.lock} className="w-3.5 h-3.5 text-[#0F9F7A]" />
    <span>Información segura y confidencial. Acceso restringido al personal autorizado.</span>
  </div>
)

// Main Component
const Search = ({ variant = 'page' }) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  // El buscador general también encuentra entidades que no son personas (sedes de
  // comedor, instituciones): se buscan acá, igual que una persona, y llevan a su ficha.
  const [entidades, setEntidades] = useState([])
  const [loading, setLoading] = useState(false)
  const [searched, setSearched] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [fuzzy, setFuzzy] = useState(false)
  const [sortBy, setSortBy] = useState('relevance')
  const [filterBarrio, setFilterBarrio] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [showInfoTooltip, setShowInfoTooltip] = useState(false)

  const [adminFilters, setAdminFilters] = useState(FILTROS_VACIOS)
  const [inundaciones, setInundaciones] = useState([])

  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState(null)
  const observerTarget = useRef(null)

  const { loading: typesLoading, data: assistanceTypes } = useAssistanceTypes()
  const { user } = useAuthStore()
  const role = String(user?.rol || '').trim().toLowerCase()
  const isAdmin = role === 'admin' || role === 'administrator'


  const debouncedQuery = useDebounce(query, 400)
  const hasAdminFilters = Object.entries(adminFilters)
    .some(([k, value]) => k !== 'incluir_hogar' && String(value || '').trim() !== '')
  const assistanceTypeOptions = assistanceTypes.categories.flatMap((category) => category.options)

  // El backend solo devuelve los tipos de entidad de los módulos que el usuario puede ver.
  const buscarEntidades = useCallback(async (texto) => {
    if (!texto || texto.length < 2 || /^\d{7,8}$/.test(texto.trim())) {
      setEntidades([])
      return
    }
    try {
      const r = await personasApi.entidades({ q: texto })
      setEntidades(r.data.data || [])
    } catch {
      setEntidades([])
    }
  }, [])

  const runSearch = useCallback(
    async (searchQuery, isFuzzy = false, pageNum = 1) => {
      const cleanQuery = (searchQuery || '').trim()
      if (cleanQuery && cleanQuery.length < 2) return
      if (!cleanQuery && !hasAdminFilters) return

      try {
        setLoading(true)
        setSearched(true)

        const isNumeric = /^\d+$/.test(cleanQuery)

        const params = { page: pageNum, limit: 25 }
        if (cleanQuery) {
          if (isNumeric) {
            params.dni = cleanQuery
          } else {
            params.q = cleanQuery
            params.fuzzy = isFuzzy
          }
        }

        if (adminFilters.tipo_asistencia) params.tipo_asistencia = adminFilters.tipo_asistencia
        if (adminFilters.detalle_asistencia.trim()) params.detalle_asistencia = adminFilters.detalle_asistencia.trim()
        if (adminFilters.nucleo_familiar.trim()) params.nucleo_familiar = adminFilters.nucleo_familiar.trim()
        if (adminFilters.convive) params.convive = adminFilters.convive
        if (adminFilters.con_familia) params.con_familia = adminFilters.con_familia
        if (adminFilters.con_asistencias) params.con_asistencias = adminFilters.con_asistencias
        if (adminFilters.comedor) params.comedor = adminFilters.comedor
        if (adminFilters.inundacion) params.inundacion = adminFilters.inundacion
        if (adminFilters.inundacion && adminFilters.incluir_hogar) params.incluir_hogar = adminFilters.incluir_hogar
        if (adminFilters.elnino) params.elnino = adminFilters.elnino

        if (pageNum === 1) buscarEntidades(cleanQuery)

        const response = await personasApi.search(params)
        const newResults = response.data.data || []
        const paginationData = response.data.pagination || null

        if (pageNum === 1) {
          setResults(newResults)
        } else {
          setResults((prev) => [...prev, ...newResults])
        }

        setPagination(paginationData)
        setHasMore(paginationData ? pageNum < paginationData.pages : false)
      } catch (error) {
        console.error('Search error:', error)
        if (pageNum === 1) setResults([])
      } finally {
        setLoading(false)
        setLoadingMore(false)
      }
    },
    [adminFilters, hasAdminFilters, buscarEntidades]
  )

  // Auto-search on debounced query change
  useEffect(() => {
    const clean = debouncedQuery.trim()
    const shouldSearch = clean.length >= 2 || hasAdminFilters
    if (shouldSearch) {
      setPage(1)
      runSearch(clean, fuzzy, 1)
    } else if (!debouncedQuery && !hasAdminFilters) {
      setResults([])
      setEntidades([])
      setSearched(false)
    }
  }, [debouncedQuery, fuzzy, hasAdminFilters, runSearch])

  // Inundaciones cargadas, para el filtro de cruces. Si falla, el filtro queda con
  // "cualquier inundación" y nada más.
  useEffect(() => {
    personasApi.opcionesCruces()
      .then((r) => setInundaciones(r.data?.data?.inundaciones || []))
      .catch(() => setInundaciones([]))
  }, [])

  // URL query param handler
  useEffect(() => {
    const q = (searchParams.get('q') || '').trim()
    if (q) setQuery(q)
  }, [searchParams])

  const handleSubmit = (e) => {
    e?.preventDefault?.()
    if (query.trim().length >= 2 || hasAdminFilters) {
      setPage(1)
      runSearch(query, fuzzy, 1)
    }
  }

  // Preset Filters
  const handleApplyPreset = (preset) => {
    if (preset === 'alta_demanda') {
      setShowFilters(true)
      setAdminFilters({ ...FILTROS_VACIOS, con_familia: 'true', con_asistencias: 'true' })
    } else if (preset === 'con_asistencia') {
      setShowFilters(true)
      setAdminFilters({ ...FILTROS_VACIOS, con_asistencias: 'true' })
    } else if (preset === 'sin_ayuda') {
      setShowFilters(true)
      setAdminFilters({ ...FILTROS_VACIOS, con_asistencias: 'false' })
    } else if (preset === 'instituciones') {
      handleSelectQuickSearch('Escuela Normal')
    }
  }

  const handleSelectQuickSearch = (val) => {
    setQuery(val)
    setPage(1)
    runSearch(val, fuzzy, 1)
  }

  const clearAdminFilters = () => setAdminFilters(FILTROS_VACIOS)

  const copyDni = async (dni) => {
    try {
      await navigator.clipboard.writeText(String(dni || ''))
      toast.success(`DNI ${dni} copiado`)
    } catch {
      toast.error('No se pudo copiar')
    }
  }

  const waLink = (tel) => {
    const digits = String(tel || '').replace(/\D/g, '')
    const full = digits.startsWith('54') ? digits : `549${digits}`
    return `https://wa.me/${full}`
  }

  // Process Results
  const processedResults = useMemo(() => {
    let items = [...results]
    if (filterBarrio) {
      items = items.filter((r) => (r.barrio || '').toLowerCase().includes(filterBarrio.toLowerCase()))
    }
    if (sortBy === 'name') {
      items.sort((a, b) => `${a.apellido} ${a.nombre}`.localeCompare(`${b.apellido} ${b.nombre}`))
    } else if (sortBy === 'recent') {
      items.sort((a, b) => {
        const da = a.ultima_asistencia ? new Date(a.ultima_asistencia).getTime() : 0
        const db = b.ultima_asistencia ? new Date(b.ultima_asistencia).getTime() : 0
        return db - da
      })
    } else if (sortBy === 'assists') {
      items.sort((a, b) => parseInt(b.total_asistencias || 0) - parseInt(a.total_asistencias || 0))
    }
    return items
  }, [results, filterBarrio, sortBy])

  const totalConDuplicado = results.filter((r) => r.tiene_duplicado_cruzado).length
  const totalConAsistencias = results.filter((r) => parseInt(r.total_asistencias || 0) > 0).length

  return (
    <div className="relative min-h-screen bg-[#F7FAF9] text-[#262626] font-sans selection:bg-[#0F9F7A]/20 pb-16">
      {/* Background Gradients & San Roque Photo Asset Mask */}
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        {/* Left Green Radial Gradient */}
        <div className="absolute -top-32 -left-32 w-128 h-128 bg-[#0F9F7A]/10 rounded-full blur-3xl" />
        {/* Top Right Cyan Radial Gradient */}
        <div className="absolute -top-24 right-0 w-112 h-112 bg-sky-400/10 rounded-full blur-3xl" />
        {/* Subtle Background Pattern */}
        <div className="absolute inset-0 bg-[radial-gradient(#0F9F7A_1px,transparent_1px)] [background-size:24px_24px] opacity-10" />
        {/* San Roque Historic Landmark Image Overlay on Right Side */}
        <div 
          className="absolute right-0 top-0 w-1/2 h-full opacity-15 bg-cover bg-right bg-no-repeat filter saturate-50 blur-[1px] [mask-image:linear-gradient(to_left,black_0%,transparent_100%)]"
          style={{ backgroundImage: `url(/museo_opt.jpg)` }}
        />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 space-y-7 pt-6 sm:pt-10">
        {/* 1. Header Institucional */}
        <SearchHero />

        {/* 2. Buscador Principal */}
        <UnifiedSearch
          query={query}
          setQuery={setQuery}
          onSearch={handleSubmit}
          loading={loading}
        />

        {/* 3. Búsqueda Fonética & Nueva Persona */}
        <PhoneticSearchToggle
          fuzzy={fuzzy}
          setFuzzy={setFuzzy}
          showInfoTooltip={showInfoTooltip}
          setShowInfoTooltip={setShowInfoTooltip}
        />

        {/* 4. Filtros Inteligentes */}
        <SmartFilters
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          adminFilters={adminFilters}
          setAdminFilters={setAdminFilters}
          clearAdminFilters={clearAdminFilters}
          assistanceTypeOptions={assistanceTypeOptions}
          typesLoading={typesLoading}
          inundaciones={inundaciones}
        />

        {/* 5. Estados Prioritarios (Pastel Cards) */}
        <StatusCard onApplyPreset={handleApplyPreset} />

        {/* 6. Búsquedas Rápidas */}
        {!searched && <QuickSearchCard onSelectQuickSearch={handleSelectQuickSearch} />}

        {/* 7. Resultados de Búsqueda */}
        {searched && (
          <div className="w-full max-w-4xl mx-auto space-y-4 pt-4 animate-in fade-in duration-300">
            {/* Toolbar Stats */}
            <div className="flex flex-wrap items-center justify-between gap-3 bg-white/95 backdrop-blur-md rounded-2xl border border-[#DCE7E5] p-4 shadow-sm">
              <div className="flex items-center gap-2">
                <span className="px-3 py-1 rounded-xl bg-[#0F9F7A] text-white text-xs font-black">
                  {pagination?.total || results.length}
                </span>
                <span className="text-xs font-bold uppercase tracking-wider text-[#64748B]">
                  Resultado{(pagination?.total || results.length) !== 1 ? 's' : ''} encontrado{(pagination?.total || results.length) !== 1 ? 's' : ''}
                </span>
              </div>

              <div className="flex items-center gap-3">
                {fuzzy && (
                  <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-teal-50 text-[#08745D] border border-teal-200">
                    🔊 Búsqueda Fonética Activa
                  </span>
                )}
                {totalConDuplicado > 0 && (
                  <span className="text-xs font-bold px-2.5 py-1 rounded-lg bg-rose-50 text-rose-700 border border-rose-200">
                    ⚠️ {totalConDuplicado} Duplicado{totalConDuplicado !== 1 ? 's' : ''}
                  </span>
                )}
                <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="text-xs font-semibold py-1">
                  <option value="relevance">Relevancia</option>
                  <option value="name">Nombre A-Z</option>
                  <option value="recent">Última asistencia</option>
                </Select>
              </div>
            </div>

            {/* Entidades que coinciden: sedes de comedor, instituciones y viajes. No son personas,
                pero se buscan desde el mismo lugar y llevan a su ficha o planilla. */}
            {entidades.length > 0 && (
              <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-[#DCE7E5] p-4 shadow-sm space-y-2">
                <span className="text-xs font-bold uppercase tracking-wider text-[#64748B]">
                  Entidades, instituciones y viajes ({entidades.length})
                </span>
                {entidades.map((e) => (
                  <Link
                    key={`${e.tipo}-${e.id}`}
                    to={e.href}
                    className="flex items-center justify-between gap-3 rounded-xl border border-transparent px-3 py-2.5 hover:border-[#0F9F7A] hover:bg-emerald-50/40 transition-colors"
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-lg" aria-hidden="true">
                        {e.tipo === 'comedor' ? '🍽️' : e.tipo === 'viaje_colectivo' ? '🚌' : tipoIcono(e.clase)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-extrabold text-[#262626]">{highlight(e.titulo, query)}</p>
                        <p className="truncate text-xs text-[#64748B]">
                          {e.tipo === 'comedor' ? 'Comedor comunitario' : e.tipo === 'viaje_colectivo' ? 'Viaje institucional / zona' : tipoLabel(e.clase)}
                          {e.detalle ? ` · ${e.detalle}` : ''}
                        </p>
                      </div>
                    </div>
                    <span className="shrink-0 text-right text-xs font-bold text-[#0F9F7A]">
                      {e.cantidad != null && <span className="block text-[11px] font-semibold text-[#64748B]">{e.cantidad} {e.cantidad_label}</span>}
                      {e.tipo === 'comedor' ? 'Ver padrón →' : e.tipo === 'viaje_colectivo' ? 'Ver viaje →' : 'Ver ficha →'}
                    </span>
                  </Link>
                ))}
              </div>
            )}

            {/* Loading Skeleton */}
            {loading && !results.length ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white/80 rounded-2xl p-5 border border-[#DCE7E5] animate-pulse flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-200" />
                    <div className="flex-1 space-y-2">
                      <div className="w-1/3 h-4 bg-slate-200 rounded" />
                      <div className="w-1/4 h-3 bg-slate-100 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : processedResults.length === 0 && entidades.length === 0 ? (
              <div className="bg-white/95 rounded-3xl border border-[#DCE7E5] p-10 text-center space-y-4 shadow-sm">
                <div className="w-16 h-16 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center mx-auto">
                  <Icon path={ICONS.search} className="w-8 h-8" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-[#262626]">Sin resultados encontrados</h3>
                  <p className="text-sm text-[#64748B] mt-1">
                    {query.trim()
                      ? <>No encontramos ningún ciudadano que coincida con &ldquo;{query}&rdquo;.</>
                      : 'Nadie cumple todos los filtros elegidos.'}
                  </p>
                </div>
                {query.trim() && <div className="pt-2">
                  <Link
                    to={`/personas/nueva?dni=${query}`}
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#0F9F7A] hover:bg-[#08745D] text-white rounded-xl font-bold text-sm shadow-sm transition-all"
                  >
                    <Icon path={ICONS.plus} className="w-4 h-4" />
                    <span>Registrar nueva persona en el padrón</span>
                  </Link>
                </div>}
              </div>
            ) : (
              /* Result Cards */
              <div className="space-y-3">
                {processedResults.map((persona) => {
                  const totalAst = parseInt(persona.total_asistencias || 0)
                  const isDuplicate = persona.tiene_duplicado_cruzado

                  return (
                    <div
                      key={persona.id}
                      onClick={() => navigate(`/persona/${persona.id}`)}
                      className={cn(
                        "group cursor-pointer bg-white/95 backdrop-blur-md rounded-2xl border p-5 transition-all hover:shadow-lg hover:shadow-teal-900/5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4",
                        isDuplicate ? "border-rose-300 bg-rose-50/30" : "border-[#DCE7E5] hover:border-[#0F9F7A]"
                      )}
                    >
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        {/* Avatar */}
                        <div className="w-12 h-12 rounded-xl bg-emerald-100 text-[#08745D] font-extrabold text-base flex items-center justify-center shrink-0 border border-emerald-200">
                          {(persona.apellido?.[0] || '?').toUpperCase()}{(persona.nombre?.[0] || '').toUpperCase()}
                        </div>

                        {/* Person Data */}
                        <div className="min-w-0 flex-1">
                          <div className="flex flex-wrap items-center gap-2">
                            <h3 className="text-base font-extrabold text-[#262626] group-hover:text-[#0F9F7A] transition-colors truncate">
                              {highlight(`${persona.apellido}, ${persona.nombre}`, query)}
                            </h3>
                            {isDuplicate && <Badge tone="danger">Duplicado Cruzado</Badge>}
                            {persona.es_evacuado && (
                              <Badge tone="social" title={persona.evac_centro ? `Centro de evacuación: ${persona.evac_centro}` : undefined}>
                                🌊 Evacuado{persona.evac_inundacion ? ` · ${persona.evac_inundacion}` : ''}
                              </Badge>
                            )}
                            {persona.es_emprendedor && <Badge tone="social">🛍️ Emprendedor</Badge>}
                            {persona.padron_anio && <Badge tone="info" title="Figura en el padrón electoral">🗳️ Padrón {persona.padron_anio}</Badge>}
                            {persona.grupo_numero && (
                              <span className="badge bg-violet-100 text-violet-800" title={`Hogar con ${persona.grupo_integrantes} integrantes (ficha → Grupo Familiar)`}>
                                👨‍👩‍👧 GF-{String(persona.grupo_numero).padStart(6, '0')} · {persona.grupo_integrantes}
                              </span>
                            )}
                            {persona.comedor && <Badge tone="social" title="Anotado/a en un comedor comunitario">🍽️ Comedor {nombreSede(persona.comedor)}</Badge>}
                            {persona.comedor_sin_tutor && <Badge tone="warning" title="Anotado/a en el comedor sin tutor/a cargado: no se ve su familia">Sin tutor/a cargado</Badge>}
                            {persona.comedor_tutor && <Badge tone="info" title="Es tutor/a de un chico anotado en el comedor">👤 Tutor/a en comedor {nombreSede(persona.comedor_tutor)}</Badge>}
                            {persona.comedor_encargado && <Badge tone="info" title="Encargado/a de la sede de comedor">🧑‍🍳 Encargado/a del comedor {nombreSede(persona.comedor_encargado)}</Badge>}
                            {persona.capacitaciones > 0 && <Badge tone="social" title="Participó en capacitaciones registradas por las áreas">🎓 {persona.capacitaciones} capacitación{persona.capacitaciones === 1 ? '' : 'es'}</Badge>}
                            {persona.produccion_entregas > 0 && <Badge tone="social" title={`Recibió de Producción: ${persona.produccion_tipos}`}>🌱 {persona.produccion_entregas} entrega{persona.produccion_entregas === 1 ? '' : 's'} de Producción</Badge>}
                            {persona.elnino_riesgo && <Badge tone={persona.elnino_riesgo === 'directa' ? 'danger' : 'warning'} title="Relevado/a por El Niño (riesgo de la vivienda)">🌧️ El Niño · {persona.elnino_riesgo}</Badge>}
                            {persona.hogar_evacuado && !persona.es_evacuado && <Badge tone="warning" title="Alguien de su hogar ya fue evacuado en una inundación">🌊 Hogar evacuado</Badge>}
                            {persona.tiene_dni_frente && persona.tiene_dni_dorso
                              ? <Badge tone="success" title="Frente y dorso cargados (Perfil → Documentos)">🪪 DNI cargado</Badge>
                              : (persona.tiene_dni_frente || persona.tiene_dni_dorso)
                                ? <Badge tone="warning" title={persona.tiene_dni_frente ? 'Falta el dorso' : 'Falta el frente'}>🪪 DNI incompleto</Badge>
                                : null}
                          </div>

                          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-[#64748B]">
                            <span className="font-mono font-bold text-[#262626]">DNI: {highlight(String(persona.dni || ''), query)}</span>
                            {persona.barrio && <span>📍 {persona.barrio}</span>}
                            {persona.telefono && <span>📞 {persona.telefono}</span>}
                          </div>
                        </div>
                      </div>

                      {/* Right Action Controls */}
                      <div className="flex items-center gap-3 w-full sm:w-auto justify-between sm:justify-end border-t sm:border-t-0 pt-3 sm:pt-0 border-slate-100">
                        <div className="text-right">
                          <div className="text-xs font-bold uppercase tracking-wider text-[#64748B]">
                            {totalAst > 0 ? `${totalAst} asistencias` : 'Sin asistencias'}
                          </div>
                          {persona.ultima_asistencia && (
                            <div className="text-[11px] text-slate-400">
                              {new Date(persona.ultima_asistencia).toLocaleDateString('es-AR')}
                            </div>
                          )}
                        </div>

                        <div className="flex items-center gap-1.5" onClick={(e) => e.stopPropagation()}>
                          <button
                            onClick={() => copyDni(persona.dni)}
                            title="Copiar DNI"
                            className="p-2 text-slate-400 hover:text-[#262626] hover:bg-slate-100 rounded-xl transition-colors"
                          >
                            <Icon path={ICONS.copy} className="w-4 h-4" />
                          </button>

                          {persona.telefono && (
                            <a
                              href={waLink(persona.telefono)}
                              target="_blank"
                              rel="noreferrer"
                              title="WhatsApp"
                              className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-xl transition-colors"
                            >
                              <Icon path={ICONS.phone} className="w-4 h-4" />
                            </a>
                          )}

                          <button
                            onClick={() => navigate(`/persona/${persona.id}`)}
                            className="px-3.5 py-2 bg-slate-100 group-hover:bg-[#0F9F7A] text-[#262626] group-hover:text-white rounded-xl text-xs font-bold transition-all flex items-center gap-1"
                          >
                            <span>Ver Ficha 360</span>
                            <Icon path={ICONS.arrowRight} className="w-3.5 h-3.5" />
                          </button>
                          {isAdmin && (
                            <button
                              type="button"
                              onClick={() => navigate(`/osint/persona/${persona.id}`)}
                              aria-label={`Revisar fuentes para ${persona.nombre} ${persona.apellido}`}
                              className="px-3 py-2 border border-[#0F9F7A]/30 text-[#08745D] hover:bg-emerald-50 rounded-xl text-xs font-bold transition-colors"
                            >
                              Fuentes
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>
        )}


        {/* 8. Footer de Seguridad */}
        <SecurityNotice />
      </div>
    </div>
  )
}

export default Search
